<template>
  <!--  Enhanced TravelTime with skeleton loading and timeout handling -->
  <div
    v-if="isListingSupportedByTravelTime && shouldShowTravelTimeSection"
    :class="$style.travelTime"
    data-testid="travel-time-surround"
  >
    <header :class="$style.travelTimeHeader">
      <h3 :class="$style.travelTimeTitle">{{ t('travelTime.title') }}</h3>
      <HgTooltip @click="trackTransparencyInfo">
        <h4 :class="$style.travelTimeTransparencyTitle">
          {{ t('travelTime.transparency.title') }}
        </h4>
        <p :class="$style.travelTimeTransparencyBody" v-html="t('travelTime.transparency.body')" />
      </HgTooltip>
    </header>

    <!--  Content-shaped skeleton loader for better CLS, LCP, and FCP -->
    <div v-if="isLoadingSurroundingInfo" :class="$style.skeletonContainer">
      <ul :class="$style.travelTimeList">
        <!-- Simulate 2-3 typical travel time items -->
        <li v-for="index in 3" :key="`skeleton-${index}`" :class="$style.travelTimePoiData">
          <HgSkeleton
            :class="$style.skeletonTitle"
            variant="rectangular"
            animation="pulse"
            width="80px"
            height="16px"
          />
          <div :class="$style.travelTimeListContent">
            <HgSkeleton
              :class="$style.skeletonIcon"
              variant="circular"
              animation="pulse"
              width="20px"
              height="20px"
            />
            <HgSkeleton
              :class="$style.skeletonTime"
              variant="rectangular"
              animation="pulse"
              width="40px"
              height="14px"
            />
            <HgSkeleton
              :class="$style.skeletonDescription"
              variant="rectangular"
              animation="pulse"
              :width="index === 1 ? '120px' : index === 2 ? '100px' : '140px'"
              height="14px"
            />
          </div>
        </li>
      </ul>
      <!-- Skeleton for description text -->
      <HgSkeleton
        :class="$style.skeletonDescriptionText"
        variant="rectangular"
        animation="pulse"
        width="100%"
        height="14px"
      />
    </div>

    <!-- Content when data is available -->
    <div v-else-if="hasTravelTimings">
      <ul :class="travelTimings.length > 1 ? $style.travelTimeList : $style.travelTimeSingleItem">
        <li v-for="timing in travelTimings" :key="timing.key" :class="$style.travelTimePoiData">
          <h4>{{ t(`travelTime.fields.${timing.key}`) }}</h4>
          <div :class="$style.travelTimeListContent">
            <HgIcon :class="$style.travelTimeListIcon" :icon="TRAVEL_TIME_ICON_MAP[timing.key]" />
            <span :class="$style.travelTimeListTime">{{ formatTimeInMin(timing.time) }}</span>
            <span :class="$style.travelTimeListDescription">{{ timing.name }}</span>
          </div>
        </li>
      </ul>
      <p :class="$style.descriptionText">
        {{ t('travelTime.description') }}
      </p>
    </div>

    <!-- Timeout/no data fallback message -->
    <div v-else-if="hasDataFetchAttempted && !hasTravelTimings" :class="$style.noDataMessage">
      <p>{{ t('travelTime.noDataAvailable') }}</p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { HgIcon, HgSkeleton, HgTooltip, Icon } from '@smg-real-estate/domus';
import { Listing } from '@smg-real-estate/hg-rets-schema';
import { computed, onBeforeUnmount, onMounted, onServerPrefetch, ref } from 'vue';

import { InteractionEvent } from '@/helpers/GTM/GTMParameters';
import { trackEventHarmonized } from '@/helpers/GTM/GTMTrack';
import { useTravelTimeStore } from '@/store/modules/travel-time';
import type { TravelTimeData } from '@/store/types/travel-time-state';
import { useI18n } from 'vue-i18n';
import { isListingSupportedByTravelTime as isListingSupportedByTravelTimeHelper } from './helper';

const { t } = useI18n();

defineOptions({
  name: 'TravelTime',
});

const TRAVEL_TIME_ICON_MAP: Record<string, Icon> = {
  pharmacy: Icon.Hospital,
  supermarket: Icon.Shopping,
  station: Icon.Commute,
  school: Icon.School,
};

interface Props {
  listing: Listing;
}
const props = defineProps<Props>();

const travelTimeStore = useTravelTimeStore();

const isLoadingSurroundingInfo = ref(false);
const hasDataFetchAttempted = ref(false);
const loadingTimeout = ref<ReturnType<typeof setTimeout> | null>(null);

const isListingSupportedByTravelTime = computed(() =>
  isListingSupportedByTravelTimeHelper(props.listing),
);
const travelTimings = computed<TravelTimeData>(() =>
  travelTimeStore.getTravelTimeData(props.listing.id!),
);
const hasTravelTimings = computed(() => travelTimings.value.length > 0);
const hasDefaultPOI = computed<boolean>(() =>
  travelTimeStore.hasDefaultPOI.some((poi) => poi.id === props.listing.id),
);
const shouldShowTravelTimeSection = computed(
  () =>
    (isLoadingSurroundingInfo.value ||
      hasTravelTimings.value ||
      (hasDataFetchAttempted.value && !hasTravelTimings.value)) &&
    hasDefaultPOI.value,
);

onServerPrefetch(async () => {
  if (!isListingSupportedByTravelTime.value) {
    return;
  }

  // Set data fetch attempted flag for SSR
  hasDataFetchAttempted.value = true;
  try {
    await travelTimeStore.fetchTravelTime({
      listingId: props.listing.id!,
      geoCoordinates: { ...props.listing.address?.geoCoordinates },
    });
  } catch (error) {
    console.error('Failed to fetch surrounding info travel time data:', error);
  }
});

onMounted(() => {
  if (!isListingSupportedByTravelTime.value) {
    return;
  }
  if (!hasTravelTimings.value) {
    fetchTravelTimeWithLoading();
  }
});

// Clean up timeout on component destruction
onBeforeUnmount(() => {
  if (loadingTimeout.value) {
    clearTimeout(loadingTimeout.value);
  }
});

const formatTimeInMin = (seconds: number) => {
  const time = seconds / 60;

  if (time < 1) {
    return '< 1 min.';
  }
  return `${Math.round(time).toString()} min.`;
};
const trackTransparencyInfo = (): void => {
  trackEventHarmonized(InteractionEvent.PDP_INTERACTION, {
    h_event_category_006: InteractionEvent.PDP_INTERACTION,
    h_event_action_007: 'openTooltip',
    h_event_label_008: 'poi_info',
  });
};

// Enhanced fetch method with loading state management
const fetchTravelTimeWithLoading = async () => {
  // Show skeleton immediately for better FCP
  isLoadingSurroundingInfo.value = true;
  hasDataFetchAttempted.value = true;
  // Set a maximum loading time to prevent skeleton from showing indefinitely
  loadingTimeout.value = setTimeout(() => {
    isLoadingSurroundingInfo.value = false;
  }, 600); // Slightly longer than API timeout to avoid flicker

  try {
    await travelTimeStore.fetchTravelTime({
      listingId: props.listing.id!,
      geoCoordinates: {
        ...props.listing?.address?.geoCoordinates,
      },
    });
  } catch (error) {
    console.error('Failed to fetch surrounding info travel time data:', error);
  } finally {
    // Clear loading state after fetch completion or timeout
    if (loadingTimeout.value) {
      clearTimeout(loadingTimeout.value);
    }
    isLoadingSurroundingInfo.value = false;
  }
};
</script>

<style lang="scss" module>
@import '@/assets/variables';

.travelTime {
  margin: $space-250 0;
}

.travelTimeHeader {
  display: flex;
  align-items: center;
}

.travelTimeTitle {
  margin: 0 $space-50 0 0;
}

@media (max-width: $viewport-medium-min) {
  .travelTimeList {
    display: grid;
    grid-template-columns: repeat(1, 1fr);

    .travelTimePoiData {
      width: 100%;

      .travelTimeListDescription {
        @include truncate-text-box(1rem, 2);
      }
    }
  }
}

@media (min-width: $viewport-medium-min) {
  .travelTimeList {
    display: grid;
    grid-template-columns: repeat(2, 1fr);

    .travelTimePoiData {
      min-width: calc(50% - 0.5rem);

      .travelTimeListDescription {
        @include truncate-text;
      }
    }
  }
}

.travelTimeList {
  @include menu-ul-reset;

  display: grid;
  gap: $space-100;
  margin-top: $space-100;

  h4 {
    margin: $space-25 0;
  }
}

.travelTimeSingleItem {
  @include menu-ul-reset;

  display: block;
  width: 100%;
  margin-top: $space-100;

  h4 {
    margin: $space-25 0;
  }

  .travelTimePoiData {
    width: 100%;
  }
}

.travelTimeListContent {
  display: flex;
  align-items: center;
  text-align: left;
}

.travelTimeListIcon {
  flex-shrink: 0;
}

.travelTimeListTime {
  display: flex;
  width: 80px;
  margin-left: $space-25;
  white-space: nowrap;
}

.travelTimeListDescription {
  width: -webkit-fill-available;
}

.travelTimeTransparencyTitle {
  margin: 0;

  @include body-1-bold;
}

.travelTimeTransparencyBody {
  margin: $space-50 0 0 0;

  @include body-3;

  br {
    display: block;
    margin: $space-25 0 0 0;
    content: '';
  }
}

.descriptionText {
  @include caption;

  color: $color-grey-100;
}

/* Content-shaped skeleton styles for better CLS prevention and UX */
.skeletonContainer {
  margin-top: $space-100;

  /* Match the grid layout of actual content */
  .travelTimeList {
    @include menu-ul-reset;

    display: grid;
    gap: $space-100;

    @media (min-width: $viewport-medium-min) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .travelTimePoiData {
    display: flex;
    flex-direction: column;
    gap: $space-25;
  }

  .travelTimeListContent {
    display: flex;
    align-items: center;
    gap: $space-25;
  }
}

.skeletonTitle {
  align-self: flex-start;
}

.skeletonIcon {
  flex-shrink: 0;
}

.skeletonTime {
  flex-shrink: 0;
}

.skeletonDescription {
  flex: 1;
}

.skeletonDescriptionText {
  margin-top: $space-100;
  max-width: 300px;
}

.noDataMessage {
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: $space-100;

  p {
    @include caption;

    color: $color-grey-100;
    margin: 0;
    text-align: center;
  }
}
</style>
